import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import {
    extractViewBox,
    getGradientColor,
    getSvgDimensions,
    isValidSvg,
    paletteColorForPathIndex,
    withMountedPathSvg,
    wrapSvgObjectInnerForHostViewport,
} from "../../utils/svgUtils";
import {
    fileNameWithFlattenSuffix,
    flattenSvgToRootPaths,
} from "../../utils/flattenSvgGeometry";
import {
    computeFillPackPlacementsAsync,
    countGridCellsForBBox,
    fillPackPlacementDepsKey,
    type FillPackPlacement,
    type FillPackPlacementMode,
    type FillPackProgress,
    type FillPackRegion,
    type FillPackRotationMode,
} from "../../utils/fillPackPlacement";
import { fingerprintSvgContent } from "../../utils/pathPopulatePlacement";
import { collectFillPackTargetsWithOptions } from "../../utils/fillPackGeometry";
import {
    paletteColorIndexForPlacement,
    randomHexColorForPlacement,
} from "../../utils/pathPopulatePlacement";
import { serializeSvgInnerAsStrokeGuides } from "../../utils/fillPackGeometry";
import type { PathPopulateGradientMode } from "./PathPopulateContext";
import { getAnchorCoordinates } from "./PathPopulateContext";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";

export type { FillPackPlacement, FillPackPlacementMode, FillPackRotationMode };

interface UploadedSvg {
    name: string;
    content: string;
}

const DEFAULT_FILL_PACK_OBJECT_SVG_CONTENT = `<?xml version="1.0" encoding="UTF-8"?>
<svg id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 461.09 461.12">
  <g id="Layer_1-2" data-name="Layer 1">
    <path d="m400.43,230.49c59.79-88.55,78.95-172.97,40.99-210.92C403.46-18.39,319.02.78,230.45,60.6,141.95.88,57.6-18.24,19.67,19.7S.85,141.98,60.57,230.49C.78,319.05-18.39,403.47,19.57,441.42c37.95,37.95,122.34,18.8,210.88-40.97,88.61,59.87,173.09,79.07,211.07,41.1s18.77-122.46-41.09-211.06Z"/>
  </g>
</svg>`;
const FILL_PACK_FIRST_USE_AUTOGEN_KEY = "object-fill-pack:first-use-autogen:v1";

interface FillPackState {
    objectSvgs: UploadedSvg[];
    pathSvg: UploadedSvg | null;
    spacing: number;
    inset: number;
    scale: number;
    error: string | null;
    rotationMode: FillPackRotationMode;
    baseRotationDeg: number;
    anchor: string;
    gradientEnabled: boolean;
    gradientMode: PathPopulateGradientMode;
    gradientColors: string[];
    maxGradientSteps: number;
    objectPlacementMode: FillPackPlacementMode;
    cellSpanMin: number;
    cellSpanMax: number;
    showPathGuides: boolean;
    pathGuideStroke: string;
    pathGuideStrokeWidth: number;
    flattenOnImport: boolean;
    explicitFillOnly: boolean;
    packRegionEnabled: boolean;
    packRegionMinX: number;
    packRegionMinY: number;
    packRegionMaxX: number;
    packRegionMaxY: number;
    setFlattenOnImport: (v: boolean) => void;
    setExplicitFillOnly: (v: boolean) => void;
    setPackRegionEnabled: (v: boolean) => void;
    setPackRegionMinX: (v: number) => void;
    setPackRegionMinY: (v: number) => void;
    setPackRegionMaxX: (v: number) => void;
    setPackRegionMaxY: (v: number) => void;
    resetPackRegionToViewBox: () => void;
    setObjectSvgs: (svgs: UploadedSvg[]) => void;
    addObjectSvg: (svg: UploadedSvg) => void;
    removeObjectSvg: (index: number) => void;
    setPathSvg: (svg: UploadedSvg | null) => void;
    setSpacing: (v: number) => void;
    setInset: (v: number) => void;
    setScale: (v: number) => void;
    setError: (e: string | null) => void;
    setRotationMode: (m: FillPackRotationMode) => void;
    setBaseRotationDeg: (v: number) => void;
    setAnchor: (a: string) => void;
    setGradientEnabled: (v: boolean) => void;
    setGradientMode: (m: PathPopulateGradientMode) => void;
    setGradientColors: (c: string[]) => void;
    setMaxGradientSteps: (n: number) => void;
    setObjectPlacementMode: (m: FillPackPlacementMode) => void;
    setCellSpanMin: (n: number) => void;
    setCellSpanMax: (n: number) => void;
    setShowPathGuides: (v: boolean) => void;
    setPathGuideStroke: (s: string) => void;
    setPathGuideStrokeWidth: (n: number) => void;
    objectInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
    pathInputRef: React.RefObject<HTMLInputElement>;
    handleFileLoad: (
        kind: "object" | "path",
        objectIndex?: number,
    ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    pathViewBox: string | null;
    regionCount: number;
    supportsFillHitTest: boolean;
    placements: FillPackPlacement[];
    estimatedPlacementCount: number;
    hasGeneratedPlacementsForCurrentParams: boolean;
    generatePlacements: () => void;
    placementComputePending: boolean;
    placementComputeProgress: FillPackProgress | null;
    cancelPlacementCompute: () => void;
    objectInnerSvgs: Array<{
        content: string;
        viewBox: string;
        dimensions: { width: number; height: number } | null;
    }>;
    scaleFactor: number;
    viewBoxAttr: string;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const FillPackContext = createContext<FillPackState | null>(null);

export function useFillPack() {
    const ctx = useContext(FillPackContext);
    if (!ctx) {
        throw new Error("useFillPack must be used within FillPackProvider");
    }
    return ctx;
}

function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;
    return svgContent.replace(
        elementRegex,
        (_match, tagName: string, attributes: string, selfClose: string) => {
            const cleanedAttributes = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

interface FillPackProviderProps {
    children: React.ReactNode;
    initialSpacing?: number;
    initialScale?: number;
}

export function FillPackProvider({
    children,
    initialSpacing = 12,
    initialScale = 40,
}: FillPackProviderProps) {
    const [objectSvgs, setObjectSvgs] = useState<UploadedSvg[]>(() => {
        const squarePreset = OBJECT_SVG_PRESETS.find((p) => p.id === "square");
        if (!squarePreset || !isValidSvg(squarePreset.svg)) return [];
        return [
            {
                name: `${squarePreset.label} (preset)`,
                content: squarePreset.svg,
            },
        ];
    });
    const [pathSvg, setPathSvg] = useState<UploadedSvg | null>(() => {
        if (!isValidSvg(DEFAULT_FILL_PACK_OBJECT_SVG_CONTENT)) return null;
        return {
            name: "default-image-fill.svg",
            content: DEFAULT_FILL_PACK_OBJECT_SVG_CONTENT,
        };
    });
    const [spacing, setSpacing] = useState(initialSpacing);
    const [inset, setInset] = useState(0);
    const [scale, setScale] = useState(initialScale);
    const [error, setError] = useState<string | null>(null);
    const [rotationMode, setRotationMode] =
        useState<FillPackRotationMode>("align");
    const [baseRotationDeg, setBaseRotationDeg] = useState(0);
    const [anchor, setAnchor] = useState("center");
    const [gradientEnabled, setGradientEnabled] = useState(false);
    const [gradientMode, setGradientMode] =
        useState<PathPopulateGradientMode>("per-path");
    const [gradientColors, setGradientColors] = useState<string[]>([
        "#000000",
        "#ff0000",
    ]);
    const [maxGradientSteps, setMaxGradientSteps] = useState(10);
    const [objectPlacementMode, setObjectPlacementMode] =
        useState<FillPackPlacementMode>("alternating");
    const [cellSpanMin, setCellSpanMinState] = useState(1);
    const [cellSpanMax, setCellSpanMaxState] = useState(1);
    const [showPathGuides, setShowPathGuides] = useState(true);
    const [pathGuideStroke, setPathGuideStroke] = useState("#888888");
    const [pathGuideStrokeWidth, setPathGuideStrokeWidth] = useState(0.5);
    const [flattenOnImport, setFlattenOnImport] = useState(false);
    const [explicitFillOnly, setExplicitFillOnly] = useState(false);
    const [packRegionEnabled, setPackRegionEnabled] = useState(false);
    const [packRegionMinX, setPackRegionMinX] = useState(0);
    const [packRegionMinY, setPackRegionMinY] = useState(0);
    const [packRegionMaxX, setPackRegionMaxX] = useState(200);
    const [packRegionMaxY, setPackRegionMaxY] = useState(200);
    const [shouldAutogenOnFirstUse, setShouldAutogenOnFirstUse] = useState(
        () => {
            if (typeof window === "undefined") return false;
            try {
                return localStorage.getItem(FILL_PACK_FIRST_USE_AUTOGEN_KEY) !== "1";
            } catch {
                return false;
            }
        },
    );

    const objectInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const flattenOnImportRef = useRef(flattenOnImport);
    flattenOnImportRef.current = flattenOnImport;
    const pathInputRef = useRef<HTMLInputElement | null>(null);

    const addObjectSvg = (svg: UploadedSvg) => {
        setObjectSvgs((prev) => [...prev, svg]);
    };

    const removeObjectSvg = (index: number) => {
        setObjectSvgs((prev) => prev.filter((_, i) => i !== index));
    };

    const clampCellSpan = (n: unknown): number => {
        const v = Math.floor(typeof n === "number" ? n : Number(n));
        if (!Number.isFinite(v)) return 1;
        return Math.max(1, Math.min(20, v));
    };

    const setCellSpanMin = useCallback((n: number) => {
        const v = clampCellSpan(n);
        setCellSpanMinState(v);
        setCellSpanMaxState((mx) => Math.max(mx, v));
    }, []);

    const setCellSpanMax = useCallback((n: number) => {
        const v = clampCellSpan(n);
        setCellSpanMaxState(v);
        setCellSpanMinState((mn) => Math.min(mn, v));
    }, []);

    const handleFileLoad =
        (kind: "object" | "path", objectIndex?: number) =>
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            const file = event.target.files?.[0];
            if (!file) return;
            setError(null);
            if (
                !file.type.includes("svg") &&
                !file.name.toLowerCase().endsWith(".svg")
            ) {
                setError("Please select an SVG file.");
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const text = reader.result as string;
                if (!isValidSvg(text)) {
                    setError("Invalid SVG file.");
                    return;
                }
                const flat = flattenOnImportRef.current
                    ? flattenSvgToRootPaths(text)
                    : null;
                const content = flat ?? text;
                const payload = {
                    name: fileNameWithFlattenSuffix(file.name, Boolean(flat)),
                    content,
                };
                if (kind === "object") {
                    if (objectIndex !== undefined) {
                        setObjectSvgs((prev) => {
                            const next = [...prev];
                            next[objectIndex] = payload;
                            return next;
                        });
                    } else {
                        addObjectSvg(payload);
                    }
                } else {
                    setPathSvg(payload);
                }
            };
            reader.onerror = () => setError("Error reading file.");
            reader.readAsText(file);
        };

    const pathViewBox = useMemo(() => {
        if (!pathSvg) return null;
        return extractViewBox(pathSvg.content)?.viewBox ?? null;
    }, [pathSvg]);

    const pathContentKey = pathSvg ? fingerprintSvgContent(pathSvg.content) : "";

    const effectivePackRegion = useMemo<FillPackRegion | null>(() => {
        if (!packRegionEnabled) return null;
        const minX = Math.min(packRegionMinX, packRegionMaxX);
        const maxX = Math.max(packRegionMinX, packRegionMaxX);
        const minY = Math.min(packRegionMinY, packRegionMaxY);
        const maxY = Math.max(packRegionMinY, packRegionMaxY);
        if (maxX <= minX || maxY <= minY) return null;
        return { minX, minY, maxX, maxY };
    }, [
        packRegionEnabled,
        packRegionMinX,
        packRegionMinY,
        packRegionMaxX,
        packRegionMaxY,
    ]);

    const resetPackRegionToViewBox = useCallback(() => {
        const vb = pathViewBox?.split(/\s+/).map(Number);
        if (vb && vb.length === 4 && vb.every((n) => Number.isFinite(n))) {
            setPackRegionMinX(vb[0]);
            setPackRegionMinY(vb[1]);
            setPackRegionMaxX(vb[0] + vb[2]);
            setPackRegionMaxY(vb[1] + vb[3]);
        }
    }, [pathViewBox]);

    const placementDepsKey = useMemo(() => {
        if (!pathSvg || objectSvgs.length === 0) return "";
        return [
            fillPackPlacementDepsKey({
                pathSvgContent: pathSvg.content,
                spacing,
                inset,
                objectCount: objectSvgs.length,
                objectPlacementMode,
                rotationMode,
                baseRotationDeg,
                cellSpanMin,
                cellSpanMax,
                explicitFillOnly,
                packRegion: effectivePackRegion,
            }),
            objectSvgs.map((o) => fingerprintSvgContent(o.content)).join(","),
        ].join("|");
    }, [
        pathSvg,
        objectSvgs,
        spacing,
        inset,
        objectPlacementMode,
        rotationMode,
        baseRotationDeg,
        cellSpanMin,
        cellSpanMax,
        explicitFillOnly,
        effectivePackRegion,
    ]);

    const placementJobGen = useRef(0);
    const placementAbortRef = useRef<AbortController | null>(null);

    const [placementAsyncPayload, setPlacementAsyncPayload] = useState<{
        key: string;
        regionCount: number;
        placements: FillPackPlacement[];
    } | null>(null);

    const [placementComputePending, setPlacementComputePending] =
        useState(false);
    const [placementComputeProgress, setPlacementComputeProgress] =
        useState<FillPackProgress | null>(null);

    const [placementGeneratedKey, setPlacementGeneratedKey] = useState<
        string | null
    >(null);

    useEffect(() => {
        setPlacementGeneratedKey(null);
        placementAbortRef.current?.abort();
        placementAbortRef.current = null;
        setPlacementComputeProgress(null);
    }, [placementDepsKey]);

    useEffect(() => {
        if (
            !pathSvg ||
            spacing <= 0 ||
            placementDepsKey === "" ||
            placementGeneratedKey !== placementDepsKey
        ) {
            return;
        }
        if (
            placementAsyncPayload &&
            placementAsyncPayload.key === placementDepsKey
        ) {
            return;
        }
        const key = placementDepsKey;
        const ac = new AbortController();
        placementAbortRef.current = ac;
        const myGen = ++placementJobGen.current;
        setPlacementComputePending(true);
        setPlacementComputeProgress({ phase: "mount", processed: 0, total: 1 });
        (async () => {
            try {
                const r = await computeFillPackPlacementsAsync({
                    pathSvgContent: pathSvg.content,
                    spacing,
                    inset,
                    objectCount: objectSvgs.length,
                    objectPlacementMode,
                    rotationMode,
                    baseRotationDeg,
                    cellSpanMin,
                    cellSpanMax,
                    explicitFillOnly,
                    packRegion: effectivePackRegion,
                    signal: ac.signal,
                    onProgress: (info) => {
                        if (placementJobGen.current !== myGen) return;
                        setPlacementComputeProgress(info);
                    },
                });
                if (placementJobGen.current !== myGen) return;
                setPlacementAsyncPayload({
                    key,
                    regionCount: r.regionCount,
                    placements: r.placements,
                });
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                }
                console.warn("Fill pack placement failed:", e);
                if (placementJobGen.current !== myGen) return;
                setPlacementAsyncPayload({
                    key,
                    regionCount: 0,
                    placements: [],
                });
            } finally {
                if (placementJobGen.current === myGen) {
                    setPlacementComputePending(false);
                    setPlacementComputeProgress(null);
                }
            }
        })();
        return () => {
            ac.abort();
        };
    }, [
        placementDepsKey,
        placementGeneratedKey,
        pathSvg,
        spacing,
        inset,
        objectSvgs,
        objectPlacementMode,
        rotationMode,
        baseRotationDeg,
        cellSpanMin,
        cellSpanMax,
        explicitFillOnly,
        effectivePackRegion,
        placementAsyncPayload,
    ]);

    const resolvedPlacements =
        placementAsyncPayload && placementAsyncPayload.key === placementDepsKey
            ? placementAsyncPayload.placements
            : [];

    const resolvedRegionCount =
        placementAsyncPayload &&
        placementAsyncPayload.key === placementDepsKey
            ? placementAsyncPayload.regionCount
            : 0;

    const hasGeneratedPlacementsForCurrentParams =
        placementGeneratedKey != null &&
        placementDepsKey !== "" &&
        placementGeneratedKey === placementDepsKey &&
        placementAsyncPayload?.key === placementDepsKey;

    const placements = hasGeneratedPlacementsForCurrentParams
        ? resolvedPlacements
        : [];

    const pathGeometryProbe = useMemo(() => {
        if (!pathSvg || spacing <= 0) {
            return {
                regionCount: 0,
                supportsFillHitTest: true,
                gridUpperBound: 0,
            };
        }
        const out = withMountedPathSvg(pathSvg.content, (root) => {
            const targets = collectFillPackTargetsWithOptions(root, {
                explicitFillOnly,
            });
            let cells = 0;
            let hit = true;
            for (const t of targets) {
                const bbox = t.el.getBBox();
                cells += countGridCellsForBBox(bbox, spacing, inset);
                if (typeof t.el.isPointInFill !== "function") {
                    hit = false;
                }
            }
            return {
                regionCount: targets.length,
                supportsFillHitTest: hit,
                gridUpperBound: cells,
            };
        });
        return (
            out ?? {
                regionCount: 0,
                supportsFillHitTest: false,
                gridUpperBound: 0,
            }
        );
    }, [pathSvg, pathContentKey, spacing, inset, explicitFillOnly]);

    const { supportsFillHitTest, gridUpperBound } = pathGeometryProbe;

    const estimatedPlacementCount = useMemo(() => {
        if (placementDepsKey === "") return 0;
        if (hasGeneratedPlacementsForCurrentParams) {
            return resolvedPlacements.length;
        }
        return pathGeometryProbe.gridUpperBound;
    }, [
        placementDepsKey,
        hasGeneratedPlacementsForCurrentParams,
        resolvedPlacements.length,
        pathGeometryProbe.gridUpperBound,
    ]);

    const generatePlacements = useCallback(() => {
        if (placementDepsKey === "") return;
        setPlacementGeneratedKey(placementDepsKey);
    }, [placementDepsKey]);

    const cancelPlacementCompute = useCallback(() => {
        placementAbortRef.current?.abort();
        placementAbortRef.current = null;
        setPlacementGeneratedKey(null);
        setPlacementComputeProgress(null);
    }, []);

    useEffect(() => {
        if (!shouldAutogenOnFirstUse) return;
        if (placementDepsKey === "") return;
        if (placementComputePending) return;
        if (placementGeneratedKey === placementDepsKey) return;
        setPlacementGeneratedKey(placementDepsKey);
    }, [
        shouldAutogenOnFirstUse,
        placementDepsKey,
        placementGeneratedKey,
        placementComputePending,
    ]);

    useEffect(() => {
        if (!shouldAutogenOnFirstUse) return;
        if (!hasGeneratedPlacementsForCurrentParams) return;
        setShouldAutogenOnFirstUse(false);
        try {
            localStorage.setItem(FILL_PACK_FIRST_USE_AUTOGEN_KEY, "1");
        } catch {
            /* ignore storage failures */
        }
    }, [shouldAutogenOnFirstUse, hasGeneratedPlacementsForCurrentParams]);

    const objectInnerSvgs = useMemo(() => {
        return objectSvgs.map((svg) => {
            const innerMatch = svg.content.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
            const innerContent = innerMatch ? innerMatch[1] : "";
            const viewBox =
                extractViewBox(svg.content)?.viewBox ?? "0 0 100 100";
            const dimensions = getSvgDimensions(svg.content);
            return { content: innerContent, viewBox, dimensions };
        });
    }, [objectSvgs]);

    const scaleFactor = useMemo(() => {
        if (objectInnerSvgs.length === 0 || scale <= 0) return scale;
        const firstDimensions = objectInnerSvgs[0]?.dimensions;
        if (!firstDimensions) return scale;
        const maxDimension = Math.max(
            firstDimensions.width,
            firstDimensions.height,
        );
        return scale / maxDimension;
    }, [objectInnerSvgs, scale]);

    const viewBoxAttr = useMemo(() => {
        const base = pathViewBox ?? "0 0 200 200";
        const parts = base.split(/\s+/).map(Number);
        if (parts.length !== 4) return base;
        let [minX, minY, w, h] = parts;
        let maxX = minX + w;
        let maxY = minY + h;
        if (placements.length > 0 && objectInnerSvgs.length > 0) {
            for (let idx = 0; idx < placements.length; idx++) {
                const p = placements[idx];
                const objectSvg = objectInnerSvgs[p.objectIndex];
                if (!objectSvg?.dimensions) continue;
                const placementScale = scaleFactor * (p.cellSpan ?? 1);
                const angleDeg = (p.angle * 180) / Math.PI;
                const angleRad = (angleDeg * Math.PI) / 180;
                const anchorCoords = getAnchorCoordinates(anchor);
                const offsetX =
                    -anchorCoords.x *
                    objectSvg.dimensions.width *
                    placementScale;
                const offsetY =
                    -anchorCoords.y *
                    objectSvg.dimensions.height *
                    placementScale;
                const objWidth = objectSvg.dimensions.width * placementScale;
                const objHeight = objectSvg.dimensions.height * placementScale;
                const cos = Math.cos(angleRad);
                const sin = Math.sin(angleRad);
                const localCorners = [
                    { x: 0, y: 0 },
                    { x: objWidth, y: 0 },
                    { x: objWidth, y: objHeight },
                    { x: 0, y: objHeight },
                ];
                for (const corner of localCorners) {
                    const dx = corner.x - -offsetX;
                    const dy = corner.y - -offsetY;
                    const rotatedX =
                        dx * cos - dy * sin + -offsetX + p.x + offsetX;
                    const rotatedY =
                        dx * sin + dy * cos + -offsetY + p.y + offsetY;
                    minX = Math.min(minX, rotatedX);
                    minY = Math.min(minY, rotatedY);
                    maxX = Math.max(maxX, rotatedX);
                    maxY = Math.max(maxY, rotatedY);
                }
            }
        }
        const padX = (maxX - minX) * 0.05;
        const padY = (maxY - minY) * 0.05;
        return `${minX - padX} ${minY - padY} ${maxX - minX + 2 * padX} ${maxY - minY + 2 * padY}`;
    }, [pathViewBox, placements, objectInnerSvgs, anchor, scaleFactor]);

    const canExport =
        Boolean(pathSvg) &&
        objectInnerSvgs.length > 0 &&
        placements.length > 0 &&
        Boolean(viewBoxAttr);

    const buildExportSvgString = (): string => {
        if (!pathSvg || objectInnerSvgs.length === 0) return "";
        const maxRi = placements.reduce(
            (a, p) => Math.max(a, p.regionIndex),
            0,
        );
        const nPl = placements.length;
        const objectElements = placements
            .map((p, idx) => {
                const objectSvg = objectInnerSvgs[p.objectIndex];
                if (!objectSvg) return "";
                const angleDeg = (p.angle * 180) / Math.PI;
                const placementScale = scaleFactor * (p.cellSpan ?? 1);
                const anchorCoords = getAnchorCoordinates(anchor);
                let offsetX = 0;
                let offsetY = 0;
                if (objectSvg.dimensions) {
                    offsetX =
                        -anchorCoords.x *
                        objectSvg.dimensions.width *
                        placementScale;
                    offsetY =
                        -anchorCoords.y *
                        objectSvg.dimensions.height *
                        placementScale;
                }
                let svgContent = objectSvg.content;
                const ri = p.regionIndex;
                if (gradientEnabled) {
                    if (gradientMode === "random-per-instance") {
                        const fillColor = randomHexColorForPlacement(ri, idx);
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            fillColor,
                        );
                    } else if (
                        gradientMode === "random-palette-per-instance" &&
                        gradientColors.length >= 1
                    ) {
                        const pi = paletteColorIndexForPlacement(
                            ri,
                            idx,
                            gradientColors.length,
                        );
                        const fillColor =
                            gradientColors[pi] ?? gradientColors[0];
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            fillColor,
                        );
                    } else if (
                        gradientMode === "random-per-path" &&
                        gradientColors.length >= 1
                    ) {
                        const fillColor = paletteColorForPathIndex(
                            gradientColors,
                            ri,
                        );
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            fillColor,
                        );
                    } else if (
                        (gradientMode === "per-path" ||
                            gradientMode === "global") &&
                        gradientColors.length >= 2
                    ) {
                        const position =
                            gradientMode === "per-path"
                                ? maxRi > 0
                                    ? ri / maxRi
                                    : 0
                                : nPl > 1
                                  ? idx / (nPl - 1)
                                  : 0;
                        const gradientColor = getGradientColor(
                            gradientColors,
                            position,
                            maxGradientSteps,
                        );
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            gradientColor,
                        );
                    }
                }
                const placedInner = objectSvg.dimensions
                    ? wrapSvgObjectInnerForHostViewport(
                          svgContent,
                          objectSvg.viewBox,
                          objectSvg.dimensions.width * placementScale,
                          objectSvg.dimensions.height * placementScale,
                      )
                    : svgContent;
                const placedTransform = `translate(${p.x + offsetX}, ${p.y + offsetY}) rotate(${angleDeg}, ${-offsetX}, ${-offsetY})${
                    objectSvg.dimensions ? "" : ` scale(${placementScale})`
                }`;
                return `<g transform="${placedTransform}">${placedInner}</g>`;
            })
            .join("\n");

        const guideSection =
            showPathGuides && pathSvg
                ? `  <g class="paths-guide">
${serializeSvgInnerAsStrokeGuides(pathSvg.content, pathGuideStroke, pathGuideStrokeWidth)}
  </g>`
                : "";

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
${guideSection}
  <g class="packed-objects">
${objectElements}
  </g>
</svg>`;
    };

    const handleDownload = () => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const objectNames = objectSvgs.map((s) => s.name).join("-");
        link.download = `${objectNames || "object"}-fill-pack-${
            pathSvg?.name || "path"
        }.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            spacing,
            inset,
            scale,
            rotationMode,
            baseRotationDeg,
            anchor,
            gradientEnabled,
            gradientMode,
            gradientColors,
            maxGradientSteps,
            objectPlacementMode,
            cellSpanMin,
            cellSpanMax,
            showPathGuides,
            pathGuideStroke,
            pathGuideStrokeWidth,
            flattenOnImport,
            explicitFillOnly,
            packRegionEnabled,
            packRegionMinX,
            packRegionMinY,
            packRegionMaxX,
            packRegionMaxY,
        }),
        [
            spacing,
            inset,
            scale,
            rotationMode,
            baseRotationDeg,
            anchor,
            gradientEnabled,
            gradientMode,
            gradientColors,
            maxGradientSteps,
            objectPlacementMode,
            cellSpanMin,
            cellSpanMax,
            showPathGuides,
            pathGuideStroke,
            pathGuideStrokeWidth,
            flattenOnImport,
            explicitFillOnly,
            packRegionEnabled,
            packRegionMinX,
            packRegionMinY,
            packRegionMaxX,
            packRegionMaxY,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.spacing != null) setSpacing(values.spacing as number);
        if (values.inset != null) setInset(values.inset as number);
        if (values.scale != null) setScale(values.scale as number);
        if (values.rotationMode != null) {
            setRotationMode(values.rotationMode as FillPackRotationMode);
        }
        if (values.baseRotationDeg != null) {
            setBaseRotationDeg(values.baseRotationDeg as number);
        }
        if (values.anchor != null) setAnchor(values.anchor as string);
        if (values.gradientEnabled != null) {
            setGradientEnabled(values.gradientEnabled as boolean);
        }
        if (values.gradientMode != null) {
            setGradientMode(values.gradientMode as PathPopulateGradientMode);
        }
        if (values.gradientColors != null) {
            setGradientColors(values.gradientColors as string[]);
        }
        if (values.maxGradientSteps != null) {
            setMaxGradientSteps(values.maxGradientSteps as number);
        }
        if (values.objectPlacementMode != null) {
            setObjectPlacementMode(
                values.objectPlacementMode as FillPackPlacementMode,
            );
        }
        if (values.cellSpanMin != null) {
            setCellSpanMin(values.cellSpanMin as number);
        }
        if (values.cellSpanMax != null) {
            setCellSpanMax(values.cellSpanMax as number);
        }
        if (values.showPathGuides != null) {
            setShowPathGuides(values.showPathGuides as boolean);
        }
        if (values.pathGuideStroke != null) {
            setPathGuideStroke(values.pathGuideStroke as string);
        }
        if (values.pathGuideStrokeWidth != null) {
            setPathGuideStrokeWidth(values.pathGuideStrokeWidth as number);
        }
        if (values.flattenOnImport != null) {
            setFlattenOnImport(Boolean(values.flattenOnImport));
        }
        if (values.explicitFillOnly != null) {
            setExplicitFillOnly(Boolean(values.explicitFillOnly));
        }
        if (values.packRegionEnabled != null) {
            setPackRegionEnabled(Boolean(values.packRegionEnabled));
        }
        if (typeof values.packRegionMinX === "number") {
            setPackRegionMinX(values.packRegionMinX);
        }
        if (typeof values.packRegionMinY === "number") {
            setPackRegionMinY(values.packRegionMinY);
        }
        if (typeof values.packRegionMaxX === "number") {
            setPackRegionMaxX(values.packRegionMaxX);
        }
        if (typeof values.packRegionMaxY === "number") {
            setPackRegionMaxY(values.packRegionMaxY);
        }
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "object-fill-pack",
        defaultPreset: {
            spacing: initialSpacing,
            inset: 0,
            scale: initialScale,
            rotationMode: "align",
            baseRotationDeg: 0,
            anchor: "center",
            gradientEnabled: false,
            gradientMode: "per-path",
            gradientColors: ["#000000", "#ff0000"],
            maxGradientSteps: 10,
            objectPlacementMode: "alternating",
            cellSpanMin: 1,
            cellSpanMax: 1,
            showPathGuides: true,
            pathGuideStroke: "#888888",
            pathGuideStrokeWidth: 0.5,
            flattenOnImport: false,
            explicitFillOnly: false,
            packRegionEnabled: false,
            packRegionMinX: 0,
            packRegionMinY: 0,
            packRegionMaxX: 200,
            packRegionMaxY: 200,
        },
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "object-fill-pack",
        {
            onPath: (svg) => {
                setError(null);
                const flat = flattenOnImportRef.current
                    ? flattenSvgToRootPaths(svg.content)
                    : null;
                setPathSvg({
                    name: fileNameWithFlattenSuffix(svg.name, Boolean(flat)),
                    content: flat ?? svg.content,
                });
            },
            onObject: (svg) => {
                setError(null);
                const flat = flattenOnImportRef.current
                    ? flattenSvgToRootPaths(svg.content)
                    : null;
                setObjectSvgs([
                    {
                        name: fileNameWithFlattenSuffix(svg.name, Boolean(flat)),
                        content: flat ?? svg.content,
                    },
                ]);
            },
        },
        { setError },
    );

    const value: FillPackState = {
        objectSvgs,
        pathSvg,
        spacing,
        inset,
        scale,
        error,
        rotationMode,
        baseRotationDeg,
        anchor,
        gradientEnabled,
        gradientMode,
        gradientColors,
        maxGradientSteps,
        objectPlacementMode,
        cellSpanMin,
        cellSpanMax,
        showPathGuides,
        pathGuideStroke,
        pathGuideStrokeWidth,
        flattenOnImport,
        explicitFillOnly,
        packRegionEnabled,
        packRegionMinX,
        packRegionMinY,
        packRegionMaxX,
        packRegionMaxY,
        setFlattenOnImport,
        setExplicitFillOnly,
        setPackRegionEnabled,
        setPackRegionMinX,
        setPackRegionMinY,
        setPackRegionMaxX,
        setPackRegionMaxY,
        resetPackRegionToViewBox,
        setCellSpanMin,
        setCellSpanMax,
        setObjectSvgs,
        addObjectSvg,
        removeObjectSvg,
        setPathSvg,
        setSpacing,
        setInset,
        setScale,
        setError,
        setRotationMode,
        setBaseRotationDeg,
        setAnchor,
        setGradientEnabled,
        setGradientMode,
        setGradientColors,
        setMaxGradientSteps,
        setObjectPlacementMode,
        setShowPathGuides,
        setPathGuideStroke,
        setPathGuideStrokeWidth,
        objectInputRefs,
        pathInputRef,
        handleFileLoad,
        pathViewBox,
        regionCount: hasGeneratedPlacementsForCurrentParams
            ? resolvedRegionCount
            : pathGeometryProbe.regionCount,
        supportsFillHitTest,
        placements,
        estimatedPlacementCount,
        hasGeneratedPlacementsForCurrentParams,
        generatePlacements,
        placementComputePending,
        placementComputeProgress,
        cancelPlacementCompute,
        objectInnerSvgs,
        scaleFactor,
        viewBoxAttr,
        canExport,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <FillPackContext.Provider value={value}>{children}</FillPackContext.Provider>
    );
}
